import React, { useState } from 'react';
import { formatCurrency } from '../utils/currencyUtils';
import { Link } from 'react-router-dom';

interface CompanyManagementProps {
    onBack: () => void;
}

interface FundingOpportunity {
    name: string;
    type: 'Grant' | 'SAFE' | 'Investor' | 'Loan';
    amount: string;
    status: 'Research' | 'Applied' | 'In Progress' | 'Completed' | 'Rejected';
    deadline?: string;
    description: string;
    nextSteps: string[];
    contact?: string;
    probability: number; // 0-100
}

interface RevenueStream {
    name: string;
    model: string;
    currentMRR: number;
    projectedMRR: number;
    customers: number;
    pricing: string;
    status: 'Planning' | 'Beta' | 'Active' | 'Scaling';
}

const CompanyManagement: React.FC<CompanyManagementProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'funding' | 'revenue' | 'roadmap'>('funding');

    const fundingOpportunities: FundingOpportunity[] = [
        {
            name: "NYSERDA Clean Energy Fund",
            type: "Grant",
            amount: "$250K - $500K",
            status: "Research",
            deadline: "August 15, 2025",
            description: "Grants for clean energy technology companies serving NY market. Perfect fit for LL97 compliance platform.",
            nextSteps: [
                "Review full RFP requirements",
                "Prepare technology demonstration",
                "Gather NY market traction data",
                "Submit Letter of Intent by July 1"
            ],
            contact: "cleancities@nyserda.ny.gov",
            probability: 75
        },
        {
            name: "NYC Economic Development Corp (NYCEDC)",
            type: "Grant",
            amount: "$100K - $300K",
            status: "Research",
            deadline: "September 30, 2025",
            description: "Technology grants for companies solving NYC infrastructure challenges. LL97 compliance is a priority area.",
            nextSteps: [
                "Schedule intro call with NYCEDC",
                "Prepare NYC building impact metrics",
                "Identify potential pilot customers",
                "Draft application outline"
            ],
            contact: "tech@nycedc.com",
            probability: 60
        },
        {
            name: "Proptech/Climate Tech VCs",
            type: "Investor",
            amount: "$500K - $2M",
            status: "Research",
            description: "Series A funding from proptech-focused VCs. Target: Fifth Wall, MetaProp, RET Ventures.",
            nextSteps: [
                "Create comprehensive pitch deck",
                "Gather customer traction metrics",
                "Build financial projections model",
                "Research warm introductions",
                "Schedule initial meetings"
            ],
            probability: 40
        },
        {
            name: "AngelList SAFE Round",
            type: "SAFE",
            amount: "$250K - $750K",
            status: "Research",
            description: "SAFE note funding from angel investors and small VCs to accelerate platform development and customer acquisition.",
            nextSteps: [
                "Determine valuation cap and discount",
                "Create AngelList syndicate profile",
                "Prepare SAFE documentation",
                "Identify lead angel investor",
                "Launch fundraising campaign"
            ],
            probability: 65
        },
        {
            name: "SBA Small Business Innovation Research (SBIR)",
            type: "Grant",
            amount: "$100K - $1.7M",
            status: "Research",
            deadline: "Rolling applications",
            description: "Federal SBIR grants for innovative technology companies. Phase I: $100K-$250K, Phase II: $750K-$1.7M.",
            nextSteps: [
                "Review SBA SBIR requirements for cleantech",
                "Identify matching federal agencies (DOE, EPA)",
                "Prepare Phase I proposal (6 months R&D)",
                "Submit to matching agency solicitation",
                "Plan Phase II development roadmap"
            ],
            contact: "sbir@sba.gov",
            probability: 45
        },
        {
            name: "SBA 7(a) Small Business Loan",
            type: "Investor",
            amount: "$25K - $5M",
            status: "Research",
            description: "SBA-guaranteed loans for working capital, equipment, and business expansion. Up to 90% guarantee for lenders.",
            nextSteps: [
                "Prepare business plan and financial projections",
                "Find SBA preferred lenders (banks, credit unions)",
                "Gather required documentation (tax returns, financials)",
                "Submit loan application",
                "Use for platform development and marketing"
            ],
            contact: "Local SBA office",
            probability: 70
        },
        {
            name: "Online Alternative Lending (Kabbage, OnDeck)",
            type: "Investor",
            amount: "$5K - $500K",
            status: "Research",
            description: "Fast approval online business loans for technology startups. Higher interest but quick access to capital.",
            nextSteps: [
                "Compare rates: Kabbage, OnDeck, BlueVine, Funding Circle",
                "Prepare financial statements and business plan",
                "Apply to multiple lenders for best terms",
                "Use for immediate growth and marketing needs",
                "Plan repayment from projected revenue"
            ],
            probability: 80
        },
        {
            name: "Equipment Financing for Tech Infrastructure",
            type: "Investor",
            amount: "$10K - $250K",
            status: "Research",
            description: "Specialized financing for technology equipment, servers, software licenses, and development tools.",
            nextSteps: [
                "Inventory needed tech equipment and software",
                "Research tech-focused lenders (Crestmark, Geneva Capital)",
                "Prepare equipment quotes and specifications",
                "Apply for equipment financing",
                "Use for platform infrastructure scaling"
            ],
            probability: 75
        }
    ];

    const revenueStreams: RevenueStream[] = [
        {
            name: "Platform Access Subscriptions",
            model: "SaaS - Monthly/Annual Subscriptions",
            currentMRR: 0,
            projectedMRR: 25000,
            customers: 0,
            pricing: "$99/mo Basic, $299/mo Pro, $999/mo Enterprise",
            status: "Planning"
        },
        {
            name: "Contractor Referral Commissions",
            model: "Transaction-based (3% of project value)",
            currentMRR: 0,
            projectedMRR: 15000,
            customers: 23,
            pricing: "3% commission on referred projects",
            status: "Beta"
        },
        {
            name: "CPACE Financing Referrals",
            model: "Transaction-based (0.75% of loan amount)",
            currentMRR: 0,
            projectedMRR: 8000,
            customers: 12,
            pricing: "0.75% commission on financing volume",
            status: "Beta"
        },
        {
            name: "White-Label Platform Licensing",
            model: "B2B Software Licensing",
            currentMRR: 0,
            projectedMRR: 12000,
            customers: 0,
            pricing: "$2,000/mo per jurisdiction license",
            status: "Planning"
        },
        {
            name: "Premium Data & Analytics",
            model: "Data-as-a-Service",
            currentMRR: 0,
            projectedMRR: 6000,
            customers: 0,
            pricing: "$500/mo for enhanced building data access",
            status: "Planning"
        }
    ];

    const formatCurrency = (amount: number) => {
        if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(1)}M`;
        } else if (amount >= 1000) {
            return `$${(amount / 1000).toFixed(0)}K`;
        }
        return `$${amount.toLocaleString()}`;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Research': return 'bg-info-100 text-info-800';
            case 'Applied': return 'bg-warning-100 text-warning-800';
            case 'In Progress': return 'bg-primary-100 text-primary-800';
            case 'Completed': return 'bg-success-100 text-success-800';
            case 'Rejected': return 'bg-danger-100 text-danger-800';
            case 'Planning': return 'bg-neutral-100 text-neutral-800';
            case 'Beta': return 'bg-warning-100 text-warning-800';
            case 'Active': return 'bg-success-100 text-success-800';
            case 'Scaling': return 'bg-secondary-100 text-secondary-800';
            default: return 'bg-neutral-100 text-neutral-800';
        }
    };

    const totalProjectedMRR = revenueStreams.reduce((sum, stream) => sum + stream.projectedMRR, 0);
    const totalProjectedARR = totalProjectedMRR * 12;

    return (
        <div className="min-h-screen bg-neutral-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-neutral-900 mb-2">Company Management</h1>
                    <p className="text-xl text-neutral-600 mb-4">Funding pipeline, revenue streams, and business development roadmap</p>
                    
                    <div className="bg-gradient-to-r from-success-50 to-primary-50 border border-success-200 rounded-lg p-4">
                        <p className="text-success-800 text-sm">
                            <strong>🚀 Mission:</strong> Transform LL97 compliance from burden to opportunity through platform access, service connections, and data-driven insights.
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-neutral-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('funding')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'funding'
                                        ? 'border-b-2 border-primary-500 text-primary-600'
                                        : 'text-neutral-500 hover:text-neutral-700'
                                }`}
                            >
                                💰 Funding Pipeline
                            </button>
                            <button
                                onClick={() => setActiveTab('revenue')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'revenue'
                                        ? 'border-b-2 border-primary-500 text-primary-600'
                                        : 'text-neutral-500 hover:text-neutral-700'
                                }`}
                            >
                                📊 Revenue Streams
                            </button>
                            <button
                                onClick={() => setActiveTab('roadmap')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'roadmap'
                                        ? 'border-b-2 border-primary-500 text-primary-600'
                                        : 'text-neutral-500 hover:text-neutral-700'
                                }`}
                            >
                                🗺️ Business Roadmap
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* Funding Pipeline Tab */}
                        {activeTab === 'funding' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-neutral-900">Funding Opportunities</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-neutral-600">Total Potential Funding</div>
                                        <div className="text-2xl font-bold text-success-600">$2M - $10M+</div>
                                        <div className="text-xs text-neutral-500 mt-1">Grants + Loans + Investment</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {fundingOpportunities.map((opportunity, index) => (
                                        <div key={index} className="bg-neutral-50 rounded-lg p-6 border border-neutral-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-neutral-900">{opportunity.name}</h3>
                                                    <div className="flex items-center space-x-3 mt-2">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                            opportunity.type === 'Grant' ? 'bg-success-100 text-success-800' :
                                                            opportunity.type === 'SAFE' ? 'bg-primary-100 text-primary-800' :
                                                            opportunity.type === 'Investor' ? 'bg-accent-100 text-accent-800' :
                                                            opportunity.type === 'Loan' ? 'bg-warning-100 text-warning-800' :
                                                            'bg-info-100 text-info-800'
                                                        }`}>
                                                            {opportunity.type}
                                                        </span>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(opportunity.status)}`}>
                                                            {opportunity.status}
                                                        </span>
                                                        <span className="text-sm text-neutral-600">
                                                            {opportunity.probability}% probability
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-lg font-bold text-neutral-900">{opportunity.amount}</div>
                                                    {opportunity.deadline && (
                                                        <div className="text-sm text-danger-600">Due: {opportunity.deadline}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <p className="text-neutral-700 mb-4">{opportunity.description}</p>

                                            {opportunity.contact && (
                                                <div className="mb-4">
                                                    <span className="text-sm font-medium text-neutral-600">Contact: </span>
                                                    <span className="text-sm text-primary-600">{opportunity.contact}</span>
                                                </div>
                                            )}

                                            <div>
                                                <h4 className="text-sm font-medium text-neutral-700 mb-2">Next Steps:</h4>
                                                <ul className="list-disc list-inside space-y-1">
                                                    {opportunity.nextSteps.map((step, stepIndex) => (
                                                        <li key={stepIndex} className="text-sm text-neutral-600">{step}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Revenue Streams Tab */}
                        {activeTab === 'revenue' && (
                            <div>
                                <div className="mb-6">
                                    <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Revenue Model Overview</h2>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                                        <div className="bg-primary-50 p-4 rounded-lg">
                                            <div className="text-2xl font-bold text-primary-600">{formatCurrency(totalProjectedMRR)}</div>
                                            <div className="text-sm text-neutral-600">Projected MRR</div>
                                        </div>
                                        <div className="bg-success-50 p-4 rounded-lg">
                                            <div className="text-2xl font-bold text-success-600">{formatCurrency(totalProjectedARR)}</div>
                                            <div className="text-sm text-neutral-600">Projected ARR</div>
                                        </div>
                                        <div className="bg-accent-50 p-4 rounded-lg">
                                            <div className="text-2xl font-bold text-accent-600">{revenueStreams.length}</div>
                                            <div className="text-sm text-neutral-600">Revenue Streams</div>
                                        </div>
                                        <div className="bg-warning-50 p-4 rounded-lg">
                                            <div className="text-2xl font-bold text-warning-600">
                                                {revenueStreams.reduce((sum, stream) => sum + stream.customers, 0)}
                                            </div>
                                            <div className="text-sm text-neutral-600">Target Customers</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {revenueStreams.map((stream, index) => (
                                        <div key={index} className="bg-neutral-50 rounded-lg p-6 border border-neutral-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-neutral-900">{stream.name}</h3>
                                                    <p className="text-sm text-neutral-600 mt-1">{stream.model}</p>
                                                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getStatusColor(stream.status)}`}>
                                                        {stream.status}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-lg font-bold text-neutral-900">
                                                        {formatCurrency(stream.projectedMRR)}/mo
                                                    </div>
                                                    <div className="text-sm text-neutral-600">
                                                        Current: {formatCurrency(stream.currentMRR)}/mo
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <span className="text-sm font-medium text-neutral-600">Pricing: </span>
                                                    <span className="text-sm text-neutral-800">{stream.pricing}</span>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-neutral-600">Target Customers: </span>
                                                    <span className="text-sm text-neutral-800">{stream.customers}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Business Roadmap Tab */}
                        {activeTab === 'roadmap' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-neutral-900 mb-6">12-Month Business Development Roadmap</h2>
                                
                                <div className="space-y-8">
                                    {/* Q2 2025 */}
                                    <div className="border-l-4 border-primary-500 pl-6">
                                        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Q2 2025 - Platform Launch & Initial Traction</h3>
                                        <ul className="space-y-2 text-sm text-neutral-700">
                                            <li>✅ Complete platform development with revenue features</li>
                                            <li>🔄 Launch freemium tier with basic LL97 analysis</li>
                                            <li>🔄 Onboard first 5 contractor partners for referral program</li>
                                            <li>📋 Apply for NYSERDA Clean Energy Fund grant</li>
                                            <li>📋 Schedule meetings with 10 target property owners</li>
                                            <li>📋 Goal: $2K MRR, 20 platform users</li>
                                        </ul>
                                    </div>

                                    {/* Q3 2025 */}
                                    <div className="border-l-4 border-success-500 pl-6">
                                        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Q3 2025 - Customer Acquisition & Funding</h3>
                                        <ul className="space-y-2 text-sm text-neutral-700">
                                            <li>📋 Launch premium subscription tiers ($99-$999/mo)</li>
                                            <li>📋 Close first major enterprise customer (F&T Group or Century Development)</li>
                                            <li>📋 Complete SAFE round funding ($250K-$500K)</li>
                                            <li>📋 Expand contractor network to 15+ partners</li>
                                            <li>📋 Add CPACE financing referral partnerships</li>
                                            <li>📋 Goal: $15K MRR, 100 platform users, first funding secured</li>
                                        </ul>
                                    </div>

                                    {/* Q4 2025 */}
                                    <div className="border-l-4 border-accent-500 pl-6">
                                        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Q4 2025 - Scale & Expansion</h3>
                                        <ul className="space-y-2 text-sm text-neutral-700">
                                            <li>📋 Launch white-label platform for other cities/states</li>
                                            <li>📋 Develop API for enterprise integrations</li>
                                            <li>📋 Begin Series A fundraising process</li>
                                            <li>📋 Expand to all 5 NYC boroughs</li>
                                            <li>📋 Add energy audit and consulting services</li>
                                            <li>📋 Goal: $35K MRR, 300 platform users, Series A term sheet</li>
                                        </ul>
                                    </div>

                                    {/* Q1 2026 */}
                                    <div className="border-l-4 border-warning-500 pl-6">
                                        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Q1 2026 - Growth & Investment</h3>
                                        <ul className="space-y-2 text-sm text-neutral-700">
                                            <li>📋 Close Series A funding ($1M-$2M)</li>
                                            <li>📋 Expand to Boston, Chicago, Washington DC markets</li>
                                            <li>📋 Launch enterprise sales team</li>
                                            <li>📋 Develop predictive analytics and AI features</li>
                                            <li>📋 Strategic partnerships with major real estate firms</li>
                                            <li>📋 Goal: $75K MRR, 1000+ platform users, multi-city presence</li>
                                        </ul>
                                    </div>
                                </div>

                                {/* Key Metrics Dashboard */}
                                <div className="mt-12 bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">Key Success Metrics to Track</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <h4 className="font-medium text-neutral-700 mb-2">Revenue Metrics</h4>
                                            <ul className="text-sm text-neutral-600 space-y-1">
                                                <li>• Monthly Recurring Revenue (MRR)</li>
                                                <li>• Customer Acquisition Cost (CAC)</li>
                                                <li>• Customer Lifetime Value (CLV)</li>
                                                <li>• Referral commission revenue</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-neutral-700 mb-2">Platform Metrics</h4>
                                            <ul className="text-sm text-neutral-600 space-y-1">
                                                <li>• Monthly Active Users (MAU)</li>
                                                <li>• Platform engagement rate</li>
                                                <li>• Conversion: free → paid</li>
                                                <li>• Contractor referral success rate</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-neutral-700 mb-2">Business Development</h4>
                                            <ul className="text-sm text-neutral-600 space-y-1">
                                                <li>• Grant applications submitted</li>
                                                <li>• Investor meetings scheduled</li>
                                                <li>• Partnership agreements signed</li>
                                                <li>• Market expansion progress</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyManagement;
